import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import type { AuthModalName } from "./AuthModalsProvider";

type OpenAuthModal = (name: AuthModalName) => void | Promise<void>;

const OPEN_MODAL_QUERY_LIST = [
  "register",
  "login",
  "fastRegistration",
  "restore",
  "valueChainLogin",
  "valueChainRegistration",
  "otpRegistration",
] as const satisfies ReadonlyArray<AuthModalName>;

type OpenModalQueryParam = (typeof OPEN_MODAL_QUERY_LIST)[number];
const OPEN_MODAL_QUERY_NAME = "openModal";
const OPEN_MODAL_SESSION_STORAGE_KEY = "authModalOpenedByQueryParam";

const isOpenModalQueryParam = (
  value: string | null,
): value is OpenModalQueryParam => {
  return (
    value !== null &&
    OPEN_MODAL_QUERY_LIST.includes(value as OpenModalQueryParam)
  );
};

export const useOpenModalByQueryParam = (open: OpenAuthModal): void => {
  const { search } = useLocation();
  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (sessionStorage.getItem(OPEN_MODAL_SESSION_STORAGE_KEY) === "true") {
      return;
    }

    const openModal = new URLSearchParams(search).get(OPEN_MODAL_QUERY_NAME);

    if (!isOpenModalQueryParam(openModal)) {
      return;
    }

    sessionStorage.setItem(OPEN_MODAL_SESSION_STORAGE_KEY, "true");
    openRef.current(openModal);
  }, [search]);
};
